import {getSessionStatusIcon} from '@github-ui/agent-sessions/components/StatusIcon'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {Label, Link, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import {addDays} from 'date-fns'
import {type ComponentPropsWithoutRef, memo, useEffect, useId, useLayoutEffect, useRef, useState} from 'react'

import type {TitleValueWithContentType} from '../../../api/columns/contracts/title'
import type {User} from '../../../api/common-contracts'
import {ItemType} from '../../../api/memex-items/item-type'
import useIsVisible from '../../../components/board/hooks/use-is-visible'
import {AssigneeStack} from '../../../components/common/assignee-stack'
import {MemexItemIcon} from '../../../components/common/memex-item-icon'
import {SanitizedHtml} from '../../../components/dom/sanitized-html'
import {getMatchingPlanningStatus} from '../../../components/item-state'
import {
  PILL_PADDING,
  PILL_STICKY_MARGIN,
  ROADMAP_ITEM_GRAB_HANDLE_HEIGHT,
  ROADMAP_ITEM_GRAB_HANDLE_WIDTH,
} from '../../../components/roadmap/constants'
import {getLatestAgentTaskState} from '../../../components/table/agent-task-utils'
import {useClearTableFocus} from '../../../components/table/hooks/use-clear-table-focus'
import {useUserSettings} from '../../../components/user-settings'
import {formatISODateString} from '../../../helpers/parsing'
import type {TimeSpan} from '../../../helpers/roadmap-helpers'
import useBodyClass from '../../../hooks/use-body-class'
import {useRoadmapTableWidth} from '../../../hooks/use-roadmap-settings'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {RoadmapResources} from '../../../strings'
import {getSortedDates} from '../date-utils'
import type {MouseState} from '../hooks/use-mouse-state'
import type {
  RoadmapDndOperation,
  RoadmapDragAndDropOperations,
  useRoadmapDragAndDrop,
} from '../hooks/use-roadmap-drag-and-drop'
import {PillAreaFocusType, useRoadmapPillAreaFocus} from '../hooks/use-roadmap-pill-area-focus'
import {useRoadmapGetViewport, useRoadmapView} from '../roadmap-view-provider'
import {getTooltipText} from '../tooltip-helper'
import styles from './roadmap-pill.module.css'
import {NavigationArrowWithFocusContext} from './roadmap-pill-buttons'
import {RoadmapCell} from './roadmap-table-layout'

const RoadmapPillDragHandle = memo(function RoadmapPillDragHandleComponent({
  ref: forwardedRef,
  tooltip,
  operation,
  active,
  containerRef,
  isDisabled,
  ...props
}: ComponentPropsWithoutRef<'div'> & {
  tooltip: string | undefined
  operation: RoadmapDndOperation
  active?: boolean
  containerRef: React.RefObject<HTMLElement | null>
  isDisabled?: boolean
} & {ref?: React.Ref<HTMLDivElement>}) {
  const [isTouchInteraction, setIsTouchInteraction] = useState(false)

  const [contentProps, portalTooltip] = usePortalTooltip({
    contentRef: containerRef,
    'aria-label': tooltip,
    direction: 'n',
    alignmentOffset: -ROADMAP_ITEM_GRAB_HANDLE_HEIGHT / 2,
    anchorOffset: -ROADMAP_ITEM_GRAB_HANDLE_WIDTH / 2,
    anchorSide: operation === 'expandLeading' ? 'inside-left' : 'inside-right',
    // prevent tooltip from showing on touch events
    open: isTouchInteraction ? false : active ? true : undefined,
  })

  const handleTouch = () => setIsTouchInteraction(true)
  const handleTouchEnd = () => {
    setTimeout(() => setIsTouchInteraction(false), 300)
  }

  return (
    <div
      {...testIdProps(`roadmap-view-item-${operation}-handle`)}
      className={clsx(
        operation !== 'move' && styles[operation],
        styles.roadmapItemGrabHandle,
        isTouchInteraction && styles.isTouchInteraction,
        isDisabled && styles.disabled,
      )}
      data-dnd-operation={operation}
      {...props}
      ref={forwardedRef}
      {...contentProps}
      onTouchStart={handleTouch}
      onTouchEnd={handleTouchEnd}
    >
      <div className={clsx(styles.dragHandle, {active})} />
      {portalTooltip}
    </div>
  )
})

export type RoadmapPillProps = {
  titleHtml: string
  renderLink: boolean
  url: string
  titleColumnValue: ColumnValue<TitleValueWithContentType>
  timeSpan: TimeSpan
  pendingTimeSpan: TimeSpan | null
  number?: number
  assignees?: Array<User>
  allowedOperations: Set<RoadmapDragAndDropOperations>
  dragProps: ReturnType<typeof useRoadmapDragAndDrop>['dragProps']
  dragDetails: ReturnType<typeof useRoadmapDragAndDrop>['dragDetails']
  index: number
  item: MemexItemModel
  mouseState?: MouseState
  expandErrorMessage?: string
}

const defaultAssignees: Array<User> = []

export const getVisiblePillCenter = (
  pillOffsetLeft: number,
  pillWidth: number,
  viewport: {left: number; right: number},
) => {
  const visibleLeft = Math.max(pillOffsetLeft, viewport.left)
  const visibleRight = Math.min(pillOffsetLeft + pillWidth, viewport.right)

  return visibleRight > visibleLeft ? (visibleLeft + visibleRight) / 2 - pillOffsetLeft : pillWidth / 2
}

/** Roadmap Pill Implementation - renders the item pill with the position and size determined by the start/end dates */
export const RoadmapPill = memo(function RoadmapPillComponent({
  titleHtml,
  renderLink,
  titleColumnValue,
  timeSpan,
  pendingTimeSpan,
  url,
  number,
  assignees = defaultAssignees,
  allowedOperations,
  dragProps,
  dragDetails,
  index,
  item,
  expandErrorMessage,
}: RoadmapPillProps) {
  const getViewport = useRoadmapGetViewport()
  const {getX} = useRoadmapView()
  const tableWidth = useRoadmapTableWidth()
  const {isVisible} = useIsVisible({ref: dragProps.node})
  const isSavePending = pendingTimeSpan !== null
  const {wrapperProps, focusType, refocusPillArea, onFocusInternalElement, linkRef, onClickPillLink} =
    useRoadmapPillAreaFocus()
  const hasExpandError = Boolean(expandErrorMessage)

  const leadingEdgeRef = useRef<HTMLDivElement | null>(null)
  const {isVisible: isLeadingEdgeVisible} = useIsVisible({ref: leadingEdgeRef})

  // Relative positioning within the visible roadmap viewport.  Add 1 to avoid fractional floating point errors.
  const [startDate, endDate] = pendingTimeSpan
    ? getSortedDates([pendingTimeSpan.start, pendingTimeSpan.end])
    : getSortedDates([timeSpan.start, timeSpan.end])
  const pillOffsetLeft = startDate ? getX(startDate) : 0
  const pillWidth = endDate ? getX(addDays(endDate, 1)) - pillOffsetLeft : 0

  const viewport = getViewport()
  const isBefore = !isVisible && pillOffsetLeft + pillWidth <= viewport.left + 1
  const isAfter = !isVisible && pillOffsetLeft >= viewport.right - 1
  const clearTableFocus = useClearTableFocus()

  useLayoutEffect(() => {
    // refocus pill element after dragging or scrolling, if cell has navigation focus
    void isVisible
    void dragProps.isDragging

    refocusPillArea()
  }, [isVisible, dragProps.isDragging, refocusPillArea])

  const {roadmapTruncateTitles} = useUserSettings()
  const titleSpan = <SanitizedHtml className={styles.SanitizedHtml}>{titleHtml}</SanitizedHtml>
  const canDrag = allowedOperations.has('allowed-drag-update')

  const agentTaskState = getLatestAgentTaskState(item.agentTasks)
  const agentTaskIcon = agentTaskState ? getSessionStatusIcon(agentTaskState) : null
  const planningStatus =
    !agentTaskIcon && hasValue(titleColumnValue) && titleColumnValue.value.contentType === ItemType.Issue
      ? getMatchingPlanningStatus(
          item.planningStatus,
          titleColumnValue.value.value.state,
          titleColumnValue.value.value.stateReason,
        )
      : undefined
  const isDragging = dragProps.isDragging && !!dragDetails
  const ghostPillWidth =
    isDragging && !isSavePending ? dragDetails.dragGhostSpan.endX - dragDetails.dragGhostSpan.startX : 0

  const pillTooltipId = useId()
  const pillTooltipText =
    getTooltipText(isDragging ? dragDetails.adjustedTimeSpan : timeSpan) ?? RoadmapResources.noDateSet
  const [pillTooltipAnchorX, setPillTooltipAnchorX] = useState(0)
  const [isPillTooltipOpen, setIsPillTooltipOpen] = useState(false)
  const isPointerOverPillRef = useRef(false)
  const isPointerDownOnPillRef = useRef(false)
  const removePointerEndListenersRef = useRef<() => void>(() => {})
  const pillTooltipRef = useRef<HTMLSpanElement>(null)
  const isPillTooltipVisible = isPillTooltipOpen && (!isDragging || dragDetails.operation === 'move')

  useBodyClass('roadmap-date-tooltip-visible', isPillTooltipVisible)

  useEffect(() => () => removePointerEndListenersRef.current(), [])

  useEffect(() => {
    if (!isPillTooltipVisible || isDragging) return

    const handleEscape = (event: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Escape dismisses transient UI before the drag-and-drop handler prevents the event.
      if (event.key === 'Escape') setIsPillTooltipOpen(false)
    }

    window.addEventListener('keydown', handleEscape, true)
    return () => window.removeEventListener('keydown', handleEscape, true)
  }, [isDragging, isPillTooltipVisible])

  const handlePillPointerEnter = (event: React.PointerEvent<HTMLAnchorElement>) => {
    if (event.pointerType === 'touch') return

    const box = event.currentTarget.getBoundingClientRect()
    isPointerOverPillRef.current = true
    setPillTooltipAnchorX(Math.min(Math.max(event.clientX - box.left, 0), box.width))
    setIsPillTooltipOpen(true)
  }

  const handlePillPointerLeave = (event: React.PointerEvent<HTMLAnchorElement>) => {
    isPointerOverPillRef.current = false
    if (event.relatedTarget instanceof Node && pillTooltipRef.current?.contains(event.relatedTarget)) return
    if (isPointerDownOnPillRef.current) return

    if (document.activeElement === event.currentTarget) {
      setPillTooltipAnchorX(getVisiblePillCenter(pillOffsetLeft, pillWidth, getViewport()))
      return
    }

    setIsPillTooltipOpen(false)
  }

  const handlePillPointerDown = (event: React.PointerEvent<HTMLAnchorElement>) => {
    const pill = event.currentTarget
    const ownerDocument = pill.ownerDocument

    removePointerEndListenersRef.current()
    isPointerDownOnPillRef.current = true

    const handlePointerEnd = () => {
      ownerDocument.removeEventListener('pointerup', handlePointerEnd, true)
      ownerDocument.removeEventListener('pointercancel', handlePointerEnd, true)
      removePointerEndListenersRef.current = () => {}
      isPointerDownOnPillRef.current = false
      if (!isPointerOverPillRef.current && ownerDocument.activeElement !== pill) {
        setIsPillTooltipOpen(false)
      }
    }
    const removePointerEndListeners = () => {
      ownerDocument.removeEventListener('pointerup', handlePointerEnd, true)
      ownerDocument.removeEventListener('pointercancel', handlePointerEnd, true)
      removePointerEndListenersRef.current = () => {}
    }

    removePointerEndListenersRef.current = removePointerEndListeners
    ownerDocument.addEventListener('pointerup', handlePointerEnd, true)
    ownerDocument.addEventListener('pointercancel', handlePointerEnd, true)
  }

  const handlePillFocus = (event: React.FocusEvent<HTMLAnchorElement>) => {
    if (!isPointerOverPillRef.current) {
      setPillTooltipAnchorX(getVisiblePillCenter(pillOffsetLeft, pillWidth, getViewport()))
    }
    setIsPillTooltipOpen(true)
    onFocusInternalElement(event)
  }

  const handlePillTooltipPointerLeave = (event: React.PointerEvent<HTMLSpanElement>) => {
    if (
      event.relatedTarget instanceof Node &&
      (event.relatedTarget === linkRef.current || linkRef.current?.contains(event.relatedTarget))
    ) {
      return
    }
    if (document.activeElement !== linkRef.current) setIsPillTooltipOpen(false)
  }

  // pillBackground provides the static styling of the pill container with absolute positioning based on start/end dates.
  const pillBackground = (
    <Label
      style={{
        width: pillWidth,
      }}
      className={styles.pillBackground}
    />
  )

  // visiblePill provides the user interaction of the pill container with absolute positioning based on start/end dates.
  const pillForeground = (
    <>
      <Label
        ref={linkRef}
        // The entire visiblePill container serves as a link to open the issue, just like clicking the actual visible link title in pillContent
        as="a"
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={onClickPillLink}
        onFocus={handlePillFocus}
        onBlur={() => {
          if (!isPointerOverPillRef.current) setIsPillTooltipOpen(false)
        }}
        style={{
          width: isDragging ? ghostPillWidth : pillWidth,
        }}
        {...dragProps.attributes}
        {...dragProps.listeners}
        aria-label={planningStatus ? `${item.getRawTitle()}, status: ${planningStatus.name}` : item.getRawTitle()}
        aria-describedby={[dragProps.attributes?.['aria-describedby'], pillTooltipId].filter(Boolean).join(' ')}
        onPointerEnter={handlePillPointerEnter}
        onPointerLeave={handlePillPointerLeave}
        onPointerDownCapture={handlePillPointerDown}
        className={clsx(
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          'roadmap-pill',
          focusType === PillAreaFocusType.PillLink && 'is-focused',
          styles.pillForeground,
          canDrag && styles.canDrag,
          isDragging && styles.isDragging,
        )}
        {...testIdProps('roadmap-view-item-pill')}
      />
      <span
        ref={pillTooltipRef}
        id={pillTooltipId}
        aria-hidden="true"
        data-visible={isPillTooltipVisible}
        style={{left: pillTooltipAnchorX}}
        className={clsx(
          styles.roadmapPillTooltip,
          index === 0 ? styles.roadmapPillTooltipSouth : styles.roadmapPillTooltipNorth,
          isPillTooltipVisible && styles.roadmapPillTooltipVisible,
        )}
      >
        <span
          className={styles.roadmapPillTooltipBubble}
          onPointerEnter={() => setIsPillTooltipOpen(true)}
          onPointerLeave={handlePillTooltipPointerLeave}
        >
          {pillTooltipText}
        </span>
      </span>
    </>
  )

  // pillContent includes the issue title that might overflow the visiblePill,
  // and gets sticky positioning on the left side when the visiblePill is partially scrolled out of view.
  const pillContent = (
    <>
      <div className={styles.Box_1}>
        {agentTaskIcon ?? (
          <MemexItemIcon
            titleColumn={titleColumnValue}
            isBlocked={item.isBlocked()}
            planningStatus={item.planningStatus}
          />
        )}
      </div>
      {renderLink ? (
        <Link
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={onClickPillLink}
          draggable={false}
          tabIndex={-1}
          className={clsx(styles.Link, isDragging && styles.isDragging)}
          {...testIdProps('roadmap-item-link')}
        >
          {titleSpan}
        </Link>
      ) : (
        titleSpan
      )}
      {number ? <span className={styles.Text}>{`#${number}`}</span> : null}
      <AssigneeStack
        assignees={assignees}
        style={{zIndex: 'var(--roadmap-pill-assignee-tooltip-z-index)'}}
        avatarProps={{onFocus: clearTableFocus}}
        {...testIdProps(`roadmap-item-assignees`)}
      />
    </>
  )

  const isLeadingActive =
    (dragDetails?.operation === 'expandLeading' && !dragDetails?.swapHandles) ||
    (dragDetails?.operation === 'expandTrailing' && dragDetails?.swapHandles)
  const isTrailingActive =
    (dragDetails?.operation === 'expandTrailing' && !dragDetails?.swapHandles) ||
    (dragDetails?.operation === 'expandLeading' && dragDetails?.swapHandles)

  const oldPositionConditions = isFeatureEnabled('memex_roadmap_drag_style') ? false : isDragging || isSavePending

  return (
    <RoadmapCell
      role="gridcell"
      {...testIdProps('roadmap-pill')}
      data-date-start={startDate ? formatISODateString(startDate) : undefined}
      data-date-end={endDate ? formatISODateString(endDate) : undefined}
      className={clsx(styles.RoadmapCell)}
    >
      <div ref={leadingEdgeRef} role={'presentation'} style={{left: pillOffsetLeft}} className={clsx(styles.Box_2)} />
      <div
        ref={dragProps.setNodeRef}
        style={{left: pillOffsetLeft}}
        className={styles.Box_3}
        {...testIdProps('roadmap-view-pill-background')}
      >
        {pillBackground}
        {!roadmapTruncateTitles.enabled && <div className={styles.pillBackgroundRight} />}
      </div>
      {isDragging && !isSavePending && <TargetPill timeSpan={dragDetails.adjustedTimeSpan} />}
      <div
        // eslint-disable-next-line react-hooks/refs
        ref={dragProps.setNodeRef}
        style={{
          left: pillOffsetLeft,
          // In Safari there is a bug where the tooltip is not rendered correctly when the pill is
          // dragged. Appending translateZ(0) forces a repaint and resolves it. Scope it to the
          // dragging state so we don't promote every pill to its own compositing layer at rest
          // (a permanent layer per pill is a Safari presentation-phase / INP regression).
          // https://stackoverflow.com/questions/3485365/how-can-i-force-webkit-to-redraw-repaint-to-propagate-style-changes
          transform: isDragging
            ? `translateX(${dragDetails.dragGhostSpan.startX ? dragDetails.dragGhostSpan.startX - pillOffsetLeft : 0}px) translateZ(0)`
            : undefined,
        }}
        className={styles.Box_5}
        {...wrapperProps}
        {...testIdProps('roadmap-view-pill-foreground')}
      >
        {pillForeground}
        {allowedOperations.has('allowed-drag-expand') && startDate && endDate ? (
          <>
            {isTrailingActive ? null : (
              <RoadmapPillDragHandle
                tooltip={
                  expandErrorMessage ||
                  getTooltipText({
                    start: isDragging ? dragDetails.adjustedTimeSpan.start : timeSpan.start,
                    startIteration: isDragging ? dragDetails.adjustedTimeSpan.startIteration : timeSpan.startIteration,
                    end: undefined,
                  }) ||
                  RoadmapResources.noDateSet
                }
                isDisabled={hasExpandError}
                operation="expandLeading"
                active={isLeadingActive}
                // eslint-disable-next-line react-hooks/refs
                ref={dragProps.setActivatorNodeRef}
                // eslint-disable-next-line react-hooks/refs
                containerRef={dragProps.node}
                // eslint-disable-next-line react-hooks/refs
                {...(!hasExpandError ? dragProps.attributes : {})}
                // eslint-disable-next-line react-hooks/refs
                {...(!hasExpandError ? dragProps.listeners : {})}
                tabIndex={-1}
                aria-label="Resize the item by dragging the earlier date"
              />
            )}
            {isLeadingActive ? null : (
              <RoadmapPillDragHandle
                tooltip={
                  expandErrorMessage ||
                  getTooltipText({
                    start: undefined,
                    end: isDragging ? dragDetails.adjustedTimeSpan.end : timeSpan.end,
                    endIteration: isDragging ? dragDetails.adjustedTimeSpan.endIteration : timeSpan.endIteration,
                  }) ||
                  RoadmapResources.noDateSet
                }
                isDisabled={hasExpandError}
                operation="expandTrailing"
                active={isTrailingActive}
                // eslint-disable-next-line react-hooks/refs
                ref={dragProps.setActivatorNodeRef}
                // eslint-disable-next-line react-hooks/refs
                containerRef={dragProps.node}
                // eslint-disable-next-line react-hooks/refs
                {...(!hasExpandError ? dragProps.attributes : {})}
                // eslint-disable-next-line react-hooks/refs
                {...(!hasExpandError ? dragProps.listeners : {})}
                tabIndex={-1}
                aria-label="Resize the item by dragging the later date"
              />
            )}
          </>
        ) : null}
      </div>
      {isVisible || isDragging ? (
        <div
          style={{
            position:
              oldPositionConditions || isLeadingEdgeVisible || roadmapTruncateTitles.enabled ? 'absolute' : 'sticky',
            left:
              oldPositionConditions || isLeadingEdgeVisible || roadmapTruncateTitles.enabled
                ? pillOffsetLeft + PILL_PADDING
                : tableWidth + PILL_STICKY_MARGIN,
            opacity: isDragging || isSavePending ? 0.3 : undefined,
          }}
          className={styles.Box_6}
          {...testIdProps('roadmap-view-item-pill-content')}
        >
          {roadmapTruncateTitles.enabled ? (
            <Truncate
              title={titleHtml}
              expandable
              style={{
                maxWidth: pillWidth - 16,
              }}
              className={styles.Box_6}
            >
              {pillContent}
            </Truncate>
          ) : (
            pillContent
          )}
        </div>
      ) : isBefore || isAfter ? (
        <NavigationArrowWithFocusContext isBefore={isBefore} startDate={startDate} endDate={endDate} />
      ) : null}
    </RoadmapCell>
  )
})

/**
 * Renders the target timespan that a pill will snap to at the end of a drag operation.
 */
const TargetPill = ({timeSpan}: {timeSpan: TimeSpan}) => {
  const {getX} = useRoadmapView()

  const [startDate, endDate] = getSortedDates([timeSpan.start, timeSpan.end])
  const pillOffsetLeft = startDate ? getX(startDate) : 0
  const pillWidth = endDate ? getX(addDays(endDate, 1)) - pillOffsetLeft : 0

  return (
    <div
      style={{
        width: pillWidth,
        left: pillOffsetLeft,
      }}
      className={clsx(styles.Box_7)}
      {...testIdProps('roadmap-view-pill-drag-target')}
    />
  )
}
